import { useEffect } from 'react'

import { Modal, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  CREATE_COMPANY_TAG,
  UPDATE_COMPANY_TAG,
} from 'src/graphql/companyTags.graphql'

interface Tag {
  tagId: string
  label: string
  category: string | null
}

interface CreateTagModalProps {
  tag?: Tag | null
  isEdit: boolean
  opened: boolean
  onClose: () => void
  onSuccess: () => void
}

const CreateTagModal = ({
  tag,
  isEdit,
  opened,
  onClose,
  onSuccess,
}: CreateTagModalProps) => {
  const MAX_TAG_LABEL_LENGTH = 30

  const form = useForm({
    initialValues: {
      label: '',
      category: '',
    },
    validateInputOnChange: true,
    validate: {
      label: (value) => {
        const trimmed = value.trim()
        if (trimmed.length === 0) {
          return 'Label is required'
        }
        if (trimmed.length > MAX_TAG_LABEL_LENGTH) {
          return `Label must not exceed ${MAX_TAG_LABEL_LENGTH} characters`
        }
        return null
      },
    },
  })

  const [createTag, { loading: creating }] = useMutation(CREATE_COMPANY_TAG, {
    onCompleted: (data) => {
      toast(
        `Tag "${data.createCompanyTag.label}" created successfully`,
        'success'
      )
      form.reset()
      onClose()
      onSuccess()
    },
    onError: (error) => {
      toast('Error creating tag', 'error')
      console.error('Error creating tag:', error)
    },
  })

  const [updateTag, { loading: updating }] = useMutation(UPDATE_COMPANY_TAG, {
    onCompleted: (data) => {
      toast(
        `Tag "${data.updateCompanyTag.label}" updated successfully`,
        'success'
      )
      form.reset()
      onClose()
      onSuccess()
    },
    onError: (error) => {
      toast('Error updating tag', 'error')
      console.error('Error updating tag:', error)
    },
  })

  useEffect(() => {
    if (tag && isEdit) {
      form.setValues({
        label: tag.label || '',
        category: tag.category || '',
      })
    } else {
      form.reset()
    }
  }, [tag, isEdit, opened])

  const handleSubmit = (values) => {
    const input = {
      label: values.label.trim(),
      category: values.category.trim() || null,
    }

    if (isEdit && tag) {
      updateTag({
        variables: {
          tagId: tag.tagId,
          input,
        },
      })
    } else {
      createTag({
        variables: {
          input,
        },
      })
    }
  }

  const handleClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEdit ? 'Edit Tag' : 'Create Tag'}
      closeOnEscape
      closeOnClickOutside
      classNames={{
        title: 'font-semibold',
        body: 'flex flex-col gap-4',
      }}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        className="flex flex-col gap-4"
      >
        <TextInput
          label="Label"
          placeholder="Enter tag label"
          required
          {...form.getInputProps('label')}
        />

        <TextInput
          label="Category"
          placeholder="Enter category (optional)"
          {...form.getInputProps('category')}
        />

        <div className="mt-4 flex justify-end gap-2">
          <DNButton variant="outline" onClick={handleClose} type="button">
            Cancel
          </DNButton>
          <DNButton
            variant="primary"
            type="submit"
            loading={creating || updating}
          >
            {isEdit ? 'Update Tag' : 'Create Tag'}
          </DNButton>
        </div>
      </form>
    </Modal>
  )
}

export default CreateTagModal
